



import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure, Select, SelectItem } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '../button/CtyButton';
import { useRef, useState, useEffect } from 'react';
import { useGetAiworkersPresignedUrlMutation, useUploadFileToS3Mutation, useUploadInternalActionMutation } from '@circuitry-ai/doc-data';
import { CtyShowToast } from '../toast/CtyToast';
import { getFileType } from '../utils/commonutils';
import Webcam from 'react-webcam';

const FileScanner = ({ actionsList, setInputValue }: { actionsList: any, setInputValue: any }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isScanning, setIsScanning] = useState(false);
    const [scanProgress, setScanProgress] = useState(0);
    const [scanDirection, setScanDirection] = useState<'down' | 'up'>('down');
    const [selectedImageType, setSelectedImageType] = useState<string>('image');
    const [getAiworkersPresignedUrl] = useGetAiworkersPresignedUrlMutation();
    const [uploadtos3] = useUploadFileToS3Mutation();
    const [uploadInternalAction] = useUploadInternalActionMutation();
    const [response, setResponse] = useState<any>(null);
    const [isCameraOpen, setIsCameraOpen] = useState<boolean>(false);
    const webcamRef = useRef<Webcam>(null);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);


    const imageTypes = [
        { key: 'image', label: 'Image' },
        { key: 'barcode', label: 'Barcode' },
        { key: 'photo', label: 'Photo' },
        { key: 'qr_code', label: 'QR Code' }
    ];

    const handleFileUpload = () => {
        onOpen();
    }

    const handleBrowseFiles = () => {
        fileInputRef.current?.click();
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const fileArray = Array.from(files);
            console.log('fileArray', fileArray);
            setSelectedFiles(fileArray);
            setIsScanning(true);
            setScanProgress(0);
            handleUpload(fileArray);
        }
    }

    const handleUpload = async (fileArray: File[]) => {
        try {
            const file = fileArray[0];
            const fileType = ['image', 'photo'].includes(selectedImageType) ? 'image' : 'barcode';
            const ocrSourceId = actionsList.find((action: any) => action.name === 'OCR Scanner')?.source_id;

            const payLoad = [
                {
                    filename: file.name,
                    source_type: 'action',
                    "metadata": {
                        "type_of_image": fileType
                    }
                }
            ]
            const uploadURL: any = await getAiworkersPresignedUrl(payLoad).unwrap();
            await uploadtos3({
                file,
                presignedUrl: uploadURL?.[0]?.presigned_url,
                CtyShowToast
            }).unwrap();
            await new Promise((resolve) => setTimeout(resolve, 2000));
            const integrationsPayload = {
                source_id: ocrSourceId,
                payload: {
                    source_extension: getFileType(file.name),
                    source_id: uploadURL?.[0]?.source_id,
                }
            }
            const response = await uploadInternalAction(integrationsPayload).unwrap();
            if (fileType === 'barcode') {
                const barcodeData = response?.[0]?.extracted_barcodes[0]?.value;
                setResponse(barcodeData);
            } else {
                let value = '';
                response?.[0]?.extracted_text.map((item: any, index: number) => {
                    value += item.text + ' ';
                })
                setResponse(value);
            }

            setIsScanning(false);
            setScanProgress(0);
            setScanDirection('down');

        } catch (error) {
            console.log('error', error);
            setIsScanning(false);
            setScanProgress(0);
            setScanDirection('down');
        }
    }

    const handleCancel = () => {
        onClose();
        setResponse(null);
        setSelectedFiles([]);
        setIsScanning(false);
        setScanProgress(0);
        setScanDirection('down');
        setSelectedImageType('image');
        setCapturedImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }

    const handleSubmit = () => {
        setInputValue(response);
        handleCancel();
    }

    // Scanning animation effect
    useEffect(() => {
        if (isScanning) {
            const interval = setInterval(() => {
                setScanProgress(prev => {
                    if (scanDirection === 'down') {
                        if (prev >= 100) {
                            setScanDirection('up');
                            return 100;
                        }
                        return prev + 2;
                    } else {
                        if (prev <= 0) {
                            setScanDirection('down');
                            return 0;
                        }
                        return prev - 2;
                    }
                });
            }, 50);

            return () => clearInterval(interval);
        }
    }, [isScanning, scanDirection]);

    const handleTakePhoto = () => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (imageSrc) {
            setIsCameraOpen(false);
            setCapturedImage(imageSrc);

            // Convert base64 to File
            const byteString = atob(imageSrc.split(',')[1]);
            const mimeString = 'image/jpeg';
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);

            for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
            }

            const file = new File([ab], 'photo.jpg', { type: mimeString });
            console.log('file', file);
        }
    };

    return (
        <>
            <CtyButton id='image-upload' isIconOnly tooltipContent="Scanner" radius="full" size="sm" variant="light" onPress={handleFileUpload}>
                <Icon className="text-primary cursor-pointer" icon="solar:scanner-broken" width={20} height={30} />
            </CtyButton>

            <Modal isOpen={isOpen} onClose={handleCancel} size="lg" isDismissable={false}>
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        <div>File Upload</div>
                        <div className="text-sm font-normal">Upload your files here</div>
                    </ModalHeader>
                    <ModalBody>
                        {selectedFiles.length === 0 && (
                            <div className="space-y-4">
                                <div className="flex flex-col gap-2">
                                    {/* <label className="text-sm font-medium text-gray-700">Select Image Type</label> */}
                                    <Select
                                        selectedKeys={[selectedImageType]}
                                        disallowEmptySelection
                                        label="Select Image Type"
                                        onSelectionChange={(keys) => {
                                            const selectedKey = Array.from(keys)[0] as string;
                                            setSelectedImageType(selectedKey);
                                            if (selectedKey === 'photo') {
                                                setIsCameraOpen(true);
                                            } else {
                                                setIsCameraOpen(false);
                                            }
                                        }}
                                        placeholder="Choose image type"
                                        className="w-full"
                                    >
                                        {imageTypes.map((type) => (
                                            <SelectItem key={type.key} value={type.key}>
                                                {type.label}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                </div>
                                {isCameraOpen &&
                                    <button
                                        className="fixed inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-50 border-none outline-none"
                                        onClick={() => setIsCameraOpen(false)} // Clicking on overlay cancels camera
                                        aria-label="Close camera overlay"
                                    >
                                        <div
                                            className="relative"
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.stopPropagation();
                                                }
                                            }}
                                            aria-label="Camera container"
                                            tabIndex={0}
                                        >
                                            {/* Close Icon */}
                                            <Icon
                                                icon="ep:circle-close-filled"
                                                width="24"
                                                height="24"
                                                onClick={() => setIsCameraOpen(false)}
                                                className="absolute top-2 right-2 cursor-pointer z-50 text-white"
                                            />
                                            <Webcam
                                                ref={webcamRef}
                                                screenshotFormat="image/jpeg"
                                                videoConstraints={{
                                                    facingMode: 'environment'
                                                }}
                                            />
                                            <button
                                                onClick={handleTakePhoto}
                                                className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-content1 rounded-full p-3"
                                            >
                                                <Icon icon="mdi:camera" width="24" height="24" />
                                            </button>
                                        </div>
                                    </button>
                                }

                                {!isCameraOpen && (
                                    <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-default-300 rounded-lg">
                                        {(capturedImage && selectedImageType === 'photo') ? (
                                            <div className="w-full">
                                                <div className="text-center mb-4">
                                                    <p className="text-sm text-default-500 mb-2">Captured Image</p>
                                                </div>
                                                <div className="flex justify-center mb-4">
                                                    <img
                                                        src={capturedImage}
                                                        alt="Captured"
                                                        className="max-w-full max-h-48 object-contain rounded border"
                                                    />
                                                </div>
                                                <div className="flex justify-center gap-2">
                                                    <CtyButton
                                                        color="danger"
                                                        variant="flat"
                                                        size="sm"
                                                        onPress={() => {
                                                            setCapturedImage(null);
                                                            setSelectedFiles([]);
                                                        }}
                                                    >
                                                        Retake Photo
                                                    </CtyButton>
                                                    <CtyButton
                                                        color="primary"
                                                        variant="flat"
                                                        size="sm"
                                                        onPress={() => {
                                                            // Convert captured image to file and process
                                                            const byteString = atob(capturedImage.split(',')[1]);
                                                            const mimeString = 'image/jpeg';
                                                            const ab = new ArrayBuffer(byteString.length);
                                                            const ia = new Uint8Array(ab);

                                                            for (let i = 0; i < byteString.length; i++) {
                                                                ia[i] = byteString.charCodeAt(i);
                                                            }

                                                            const file = new File([ab], 'photo.jpg', { type: mimeString });

                                                            // Create a synthetic event to pass to handleFileChange
                                                            const syntheticEvent = {
                                                                target: {
                                                                    files: [file]
                                                                }
                                                            } as React.ChangeEvent<HTMLInputElement>;

                                                            handleFileChange(syntheticEvent);
                                                        }}
                                                    >
                                                        Process Image
                                                    </CtyButton>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <Icon icon="solar:upload-linear" width={48} height={48} className="text-default-400 mb-4" />
                                                <p className="text-default-500 mb-2">Drag and drop files here or</p>
                                                <CtyButton color="primary" variant="flat" size="sm" onPress={handleBrowseFiles}>
                                                    Browse Files
                                                </CtyButton>
                                            </>
                                        )}
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                            accept="image/*"
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedFiles.length > 0 && (
                            <div className="mt-4">
                                {isScanning && <div className="text-center mb-4">
                                    <div className="flex items-center justify-center gap-2 mb-2">
                                        <Icon icon="solar:scanner-linear" className="text-success" width={20} height={20} />
                                        <span className="text-lg font-medium text-success">Extracting data...</span>
                                    </div>
                                    <p className="text-sm text-default-500">Please wait while we process your {selectedImageType}</p>
                                </div>}
                                {response && <div className='flex text-center justify-center text-md font-medium text-primary mb-3'>{response}</div>}
                                <div className="space-y-2">
                                    {selectedFiles.map((file, index) => (
                                        <div key={index + 'file-scanner'} className="flex flex-col items-center p-4 bg-default-100 rounded relative">
                                            <div className="relative">
                                                <img
                                                    src={URL.createObjectURL(file)}
                                                    alt={file.name}
                                                    className="max-w-full max-h-64 object-contain rounded mb-2"
                                                />
                                                {isScanning && (
                                                    <div className="absolute inset-0 bg-success bg-opacity-20 rounded">
                                                        <div
                                                            className="absolute left-0 right-0 h-1 bg-success animate-pulse"
                                                            style={{
                                                                top: `${scanProgress}%`,
                                                                transition: 'top 1s linear'
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center justify-between w-full">
                                                <span className="text-sm">{file.name}</span>
                                                <span className="text-xs text-default-500">
                                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-2">
                                                <span className="text-xs text-default-500">Type:</span>
                                                <span className="text-xs font-medium text-primary">{imageTypes.find(type => type.key === selectedImageType)?.label}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <CtyButton color="danger" variant="light" onPress={handleCancel}>
                            Cancel
                        </CtyButton>
                        <CtyButton color="primary" variant="light" onPress={handleSubmit}>
                            Submit
                        </CtyButton>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    )
}

export { FileScanner }